/* eslint-disable react/destructuring-assignment */
import { Flex, FlexProps } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { OFText } from "design_system/components/OFText";
import { OrderStatus, PaymentStatus, PrescriptionStatus } from "graphql/generated";
import { colors } from "src/colors";

export enum ExternalStatus {
  approved = "approved",
  completed = "completed",
  saved = "saved",
  rejected = "rejected",

  // plan page
  outOfStock = "out_of_stock",
  oneTimePurchase = "one_time_purchase",

  // ops
  pendingConfirmation = "pending_confirmation"
}

export type StatusTypes = ExternalStatus | OrderStatus | PaymentStatus | PrescriptionStatus;

type Props = FlexProps & {
  size?: "large" | "small";
  status: StatusTypes;
};

const SIZES = {
  large: { minW: "180px", h: "24px" },
  small: { minW: "124px", h: "24px" }
};

export const BADGE_STATUSES: {
  [key in StatusTypes]: { text: string; bg: string; color?: string };
} = {
  [OrderStatus.Active]: { text: "Active", bg: colors.green[500], color: colors.white.off },
  [OrderStatus.Cancel]: { text: "Cancelled", bg: colors.grey[800], color: colors.white.off },
  [OrderStatus.CancelBooking]: {
    text: "Cancel Booking",
    bg: colors.green[400]
  },
  [OrderStatus.Cancelled]: { text: "Cancelled", bg: colors.grey[800], color: colors.white.off },
  [OrderStatus.Deleted]: { text: "Deleted", bg: colors.red[400] },
  [OrderStatus.OnHold]: { text: "On hold", bg: colors.green[200] },
  [OrderStatus.Paused]: { text: "Paused", bg: colors.green[200] },
  [OrderStatus.Pending]: { text: "Pending", bg: colors.blue[400], color: colors.white.off },
  [OrderStatus.PendingBooking]: {
    text: "Pending Booking",
    bg: colors.blue[400],
    color: colors.white.off
  },
  [OrderStatus.PendingDelivery]: {
    text: "Pending Delivery",
    bg: colors.blue[500],
    color: colors.white.off
  },
  [OrderStatus.Verified]: {
    text: "Pending Delivery",
    bg: colors.blue[500],
    color: colors.white.off
  },
  [OrderStatus.PendingProcessing]: {
    text: "Pending Delivery",
    bg: colors.blue[500],
    color: colors.white.off
  },
  [OrderStatus.PendingPayment]: {
    text: "Pending Payment",
    bg: colors.red[400],
    color: colors.white.off
  },
  [OrderStatus.PendingPrescription]: {
    text: "Pending RX",
    bg: colors.yellow[100]
  },
  [OrderStatus.PendingResults]: {
    text: "Pending Results",
    bg: colors.purple[400]
  },
  [OrderStatus.PendingSamples]: {
    text: "Pending Samples",
    bg: colors.purple[100]
  },
  [OrderStatus.PrescriptionChange]: {
    text: "Rx Changed",
    bg: colors.yellow[400]
  },
  [OrderStatus.PrescriptionRejected]: {
    text: "Rejected",
    bg: colors.grey[800],
    color: colors.white.off
  },
  [OrderStatus.Refunded]: { text: "Refunded", bg: colors.yellow[400] },
  [OrderStatus.ResultsReady]: {
    text: "Results Ready",
    bg: colors.purple[400]
  },
  [OrderStatus.SamplesPickupScheduled]: {
    text: "Samples Pick Up Scheduled",
    bg: colors.yellow[400]
  },
  [OrderStatus.PendingPrescriptionFu]: {
    text: "Pending Follow Up",
    bg: colors.blue[200]
  },
  [OrderStatus.PendingBookingFu]: {
    text: "Follow Up Due",
    bg: colors.blue[500],
    color: colors.white.off
  },
  [ExternalStatus.approved]: {
    text: "Approved",
    bg: colors.green[300]
  },
  [ExternalStatus.completed]: {
    text: "Completed",
    bg: colors.blue[300]
  },
  [ExternalStatus.rejected]: {
    text: "Rejected",
    bg: colors.grey[800],
    color: colors.white.off
  },
  [ExternalStatus.saved]: { text: "Saved", bg: colors.yellow[200] },
  [ExternalStatus.oneTimePurchase]: { text: "One Time Purchase", bg: colors.green[300] },
  [ExternalStatus.outOfStock]: { text: "Out of Stock", bg: colors.grey[400] },

  [ExternalStatus.pendingConfirmation]: {
    text: "Pending Confirmation",
    bg: colors.yellow[400]
  },
  [PaymentStatus.Paid]: {
    text: "Paid",
    bg: colors.green[500],
    color: colors.white.off
  },
  [PaymentStatus.Unpaid]: {
    text: "UnPaid",
    bg: colors.orange[400],
    color: colors.white.off
  },
  [PaymentStatus.Fail]: {
    text: "UnPaid",
    bg: colors.red[400],
    color: colors.white.off
  },
  [PrescriptionStatus.Approve]: {
    text: "Approved",
    bg: colors.green[500],
    color: colors.white.off
  },
  [PrescriptionStatus.Prescribed]: {
    text: "Prescribed",
    bg: colors.green[500],
    color: colors.white.off
  },
  [PrescriptionStatus.Reject]: {
    text: "Rejected",
    bg: colors.grey[800],
    color: colors.white.off
  },
  [PrescriptionStatus.RxChange]: {
    text: "Rx Changed",
    bg: colors.purple[400],
    color: colors.white.off
  },
  [PrescriptionStatus.NoShow]: {
    text: "No Show",
    bg: colors.purple[400],
    color: colors.white.off
  },
  [PrescriptionStatus.ChangeConsult]: {
    text: "Change Consult",
    bg: colors.purple[400],
    color: colors.white.off
  },
  [PrescriptionStatus.Cancel]: {
    text: "Cancel",
    bg: colors.purple[400],
    color: colors.white.off
  },
  [OrderStatus.PendingUploadIc]: {
    text: "Pending Upload IC",
    bg: colors.purple[400],
    color: colors.white.off
  },
  [OrderStatus.PendingCheckout]: {
    text: "Pending Checkout",
    bg: colors.green[400],
    color: colors.white.off
  },
  [OrderStatus.PendingHumansaCheck]: {
    text: "Pending Humansa",
    bg: colors.green[400],
    color: colors.white.off
  }
};

export const StatusBadge = ({ status, size = "large", ...props }: Props) => {
  const { t } = useTranslation(["common"]);

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      borderRadius="12px"
      bg={BADGE_STATUSES[status]?.bg}
      {...SIZES[size]}
      {...props}
    >
      <OFText
        color={BADGE_STATUSES[status]?.color || colors.black[900]}
        textStyle="labels"
        textTransform="uppercase"
      >
        {t(`common:status_badge.${status}`, {
          defaultValue: BADGE_STATUSES[status]?.text
        }).toLocaleUpperCase()}
      </OFText>
    </Flex>
  );
};
